import { useState, useEffect } from "react";
import { db } from "../utils/firebase";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    setDoc,
    increment,
    serverTimestamp
} from "firebase/firestore";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const GSTReports = () => {

    const email = localStorage.getItem("user");

    const [selectedDate, setSelectedDate] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");

    const [dailyRemaining, setDailyRemaining] = useState(2);
    const [monthlyRemaining, setMonthlyRemaining] = useState(5);

    const [businessState, setBusinessState] = useState("");

    const [loadingDaily, setLoadingDaily] = useState(false);
    const [loadingMonthly, setLoadingMonthly] = useState(false);
    const [businessGST, setBusinessGST] = useState("");

    /* NEW STATE FOR GST DASHBOARD */
    const [taxStats, setTaxStats] = useState({
        cgst: 0,
        sgst: 0,
        vat: 0,
        totalGST: 0
    });

    const todayKey = new Date().toISOString().slice(0, 10).replace(/-/g, "");

    const now = new Date();

    const months = [
        new Date(now.getFullYear(), now.getMonth() - 1, 1),
        new Date(now.getFullYear(), now.getMonth() - 2, 1),
        new Date(now.getFullYear(), now.getMonth() - 3, 1),
    ];

    const monthOptions = months.map(d => ({
        label: d.toLocaleString("default", { month: "long", year: "numeric" }),
        value: `${d.getFullYear()}-${d.getMonth() + 1}`
    }));


    useEffect(() => {
        checkDailyLimit();
        fetchBusinessState();
        fetchMonthlyStats();   // NEW
    }, []);


    const fetchBusinessState = async () => {

        const q = query(
            collection(db, "userDetails"),
            where("email", "==", email)
        );

        const snap = await getDocs(q);

        if (!snap.empty) {
            const data = snap.docs[0].data();
            setBusinessState(data.state || "");
            setBusinessGST(data.gstNumber || "");
        }
    };


    /* NEW FUNCTION — CALCULATE MONTHLY GST */

    const fetchMonthlyStats = async () => {

        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        const q = query(
            collection(db, "business", email, "billing"),
            where("year", "==", year),
            where("month", "==", month)
        );

        const snap = await getDocs(q);

        if (snap.empty) return;

        let cgst = 0;
        let sgst = 0;
        let vat = 0;

        snap.docs.forEach(docSnap => {

            const data = docSnap.data();

            data.items.forEach(item => {

                const taxable = item.basePrice || 0;

                if (item.gstPercent > 0) {

                    const gst = taxable * item.gstPercent / 100;

                    cgst += gst / 2;
                    sgst += gst / 2;
                }

                if (item.vatPercent > 0) {

                    vat += taxable * item.vatPercent / 100;

                }

            });

        });

        setTaxStats({
            cgst,
            sgst,
            vat,
            totalGST: cgst + sgst + vat
        });

    };


    const checkDailyLimit = async () => {

        const ref = doc(db, "business", email, "reportUsage", `daily_${todayKey}`);
        const snap = await getDoc(ref);

        if (snap.exists()) {
            const used = snap.data().downloads || 0;
            setDailyRemaining(Math.max(2 - used, 0));
        }
    };


    const checkMonthlyLimit = async (monthValue) => {

        const [year, month] = monthValue.split("-");
        const monthKey = `${year}${String(month).padStart(2, "0")}`;

        const ref = doc(
            db,
            "business",
            email,
            "reportUsage",
            `monthly_${monthKey}`
        );

        const snap = await getDoc(ref);

        if (snap.exists()) {
            const used = snap.data().downloads || 0;
            setMonthlyRemaining(Math.max(5 - used, 0));
        } else {
            setMonthlyRemaining(5);
        }
    };


    const generateExcel = (rows, summary, filename) => {

        const workbook = XLSX.utils.book_new();

        const header = [
            ["Zafflo GST Sales Report"],
            [`Business: ${email}`],
            [`GSTIN: ${businessGST}`],
            [`Place Of Supply: ${businessState}`],
            [`Report Type: GST Sales Register`],
            [`Generated: ${new Date().toLocaleString()}`],
            []
        ];

        const sheet = XLSX.utils.json_to_sheet([]);

        XLSX.utils.sheet_add_aoa(sheet, header, { origin: "A1" });
        XLSX.utils.sheet_add_json(sheet, rows, { origin: "A6" });

        XLSX.utils.book_append_sheet(workbook, sheet, "GST_Report");


        const summaryRows = [
            { Metric: "Total Taxable Value", Amount: summary.taxable },
            { Metric: "Total CGST", Amount: summary.cgst },
            { Metric: "Total SGST", Amount: summary.sgst },
            { Metric: "Total VAT", Amount: summary.vat },
            { Metric: "Total Invoice Value", Amount: summary.total }
        ];

        const summarySheet = XLSX.utils.json_to_sheet(summaryRows);

        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");


        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(blob, filename);
    };


    const processDocuments = (snap) => {

        let invoiceGSTRate = 0;
        let sr = 1;

        const rows = [];

        let taxableTotal = 0;
        let cgstTotal = 0;
        let sgstTotal = 0;
        let vatTotal = 0;
        let invoiceTotal = 0;

        snap.docs.forEach(docSnap => {

            const data = docSnap.data();

            const invoiceDate = new Date(
                data.invoiceDate.seconds * 1000
            ).toLocaleDateString("en-IN");

            let invoiceTaxable = 0;
            let invoiceCGST = 0;
            let invoiceSGST = 0;
            let invoiceVAT = 0;
            let invoiceFinal = 0;

            data.items.forEach(item => {

                const taxable = item.basePrice || 0;

                if (item.gstPercent) {
                    invoiceGSTRate = item.gstPercent;
                }

                let cgst = 0;
                let sgst = 0;
                let vat = 0;

                if (item.gstPercent > 0) {

                    const gst = taxable * item.gstPercent / 100;

                    cgst = gst / 2;
                    sgst = gst / 2;
                }

                if (item.vatPercent > 0) {

                    vat = taxable * item.vatPercent / 100;

                }

                invoiceTaxable += taxable;
                invoiceCGST += cgst;
                invoiceSGST += sgst;
                invoiceVAT += vat;
                invoiceFinal += item.finalPrice;

            });


            rows.push({
                SrNo: sr++,
                InvoiceNo: data.invoiceNo,
                InvoiceDate: invoiceDate,
                PlaceOfSupply: businessState,
                GSTPercent: invoiceGSTRate,
                TaxableValue: invoiceTaxable.toFixed(2),
                CGST: invoiceCGST.toFixed(2),
                SGST: invoiceSGST.toFixed(2),
                VAT: invoiceVAT.toFixed(2),
                InvoiceTotal: invoiceFinal.toFixed(2),
                PaymentMode: data.paymentMode
            });


            taxableTotal += invoiceTaxable;
            cgstTotal += invoiceCGST;
            sgstTotal += invoiceSGST;
            vatTotal += invoiceVAT;
            invoiceTotal += invoiceFinal;

        });

        return {
            rows,
            summary: {
                taxable: taxableTotal,
                cgst: cgstTotal,
                sgst: sgstTotal,
                vat: vatTotal,
                total: invoiceTotal
            }
        };
    };


    const downloadDaily = async () => {

        if (!selectedDate) {
            alert("Select a date.");
            return;
        }

        setLoadingDaily(true);

        const key = selectedDate.replace(/-/g, "");

        const q = query(
            collection(db, "business", email, "billing"),
            where("businessDate", "==", key)
        );

        const snap = await getDocs(q);

        if (snap.empty) {
            alert("No records found.");
            setLoadingDaily(false);
            return;
        }

        const { rows, summary } = processDocuments(snap);

        generateExcel(rows, summary, `Daily_GST_${key}.xlsx`);

        const ref = doc(db, "business", email, "reportUsage", `daily_${todayKey}`);

        await setDoc(ref, {
            downloads: increment(1),
            updatedAt: serverTimestamp()
        }, { merge: true });

        setDailyRemaining(prev => Math.max(prev - 1, 0));
        setLoadingDaily(false);
    };


    const downloadMonthly = async () => {

        if (!selectedMonth) {
            alert("Select month.");
            return;
        }

        setLoadingMonthly(true);

        const [year, month] = selectedMonth.split("-").map(Number);

        const q = query(
            collection(db, "business", email, "billing"),
            where("year", "==", year),
            where("month", "==", month)
        );

        const snap = await getDocs(q);

        if (snap.empty) {
            alert("No records found.");
            setLoadingMonthly(false);
            return;
        }

        const { rows, summary } = processDocuments(snap);

        generateExcel(rows, summary, `Monthly_GST_${year}_${month}.xlsx`);

        const monthKey = `${year}${String(month).padStart(2, "0")}`;

        const ref = doc(
            db,
            "business",
            email,
            "reportUsage",
            `monthly_${monthKey}`
        );

        await setDoc(ref, {
            downloads: increment(1),
            updatedAt: serverTimestamp()
        }, { merge: true });

        setMonthlyRemaining(prev => Math.max(prev - 1, 0));
        setLoadingMonthly(false);
    };


    return (

        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">

            <div className="space-y-1">
                <h1 className="text-3xl font-bold text-gray-800">
                    GST & Tax Reports
                </h1>

                <p className="text-sm text-gray-500">
                    Generate GST compliant sales reports for your business.
                </p>
            </div>


            {/* GST OVERVIEW */}

            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">

                <div className="flex justify-between items-center mb-6">

                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            GST Overview
                        </h2>
                        <p className="text-xs text-gray-500">
                            Current month tax summary
                        </p>
                    </div>

                    <div className="text-right">
                        <p className="text-xs text-gray-500">Total GST Collected</p>
                        <p className="text-2xl font-bold text-green-600">
                            ₹{taxStats.totalGST.toFixed(2)}
                        </p>
                    </div>

                </div>

                <div className="grid grid-cols-3 gap-4">

                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-gray-500">CGST</p>
                        <p className="text-lg font-semibold">
                            ₹{taxStats.cgst.toFixed(2)}
                        </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-gray-500">SGST</p>
                        <p className="text-lg font-semibold">
                            ₹{taxStats.sgst.toFixed(2)}
                        </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-gray-500">VAT</p>
                        <p className="text-lg font-semibold">
                            ₹{taxStats.vat.toFixed(2)}
                        </p>
                    </div>

                </div>

            </div>


            {/* REPORT CARDS */}

            <div className="grid md:grid-cols-2 gap-6">

                {/* DAILY REPORT */}

                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 space-y-5 hover:shadow-md transition">

                    <div className="flex items-center justify-between">

                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">
                                Daily GST Report
                            </h2>

                            <p className="text-xs text-gray-500 mt-1">
                                Download GST report for a specific date
                            </p>
                        </div>

                        <div className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-600">
                            {dailyRemaining}/2 left
                        </div>

                    </div>


                    <div className="flex gap-3">

                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                        />

                        <button
                            onClick={downloadDaily}
                            disabled={loadingDaily || dailyRemaining === 0}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition 
            ${loadingDaily || dailyRemaining === 0
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                                }`}
                        >
                            {loadingDaily ? "Preparing..." : "Download"}
                        </button>

                    </div>

                    <p className="text-xs text-gray-400">
                        Remaining downloads today: {dailyRemaining} of 2
                    </p>

                </div>



                {/* MONTHLY REPORT */}

                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 space-y-5 hover:shadow-md transition">

                    <div className="flex items-center justify-between">

                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">
                                Monthly GST Report
                            </h2>

                            <p className="text-xs text-gray-500 mt-1">
                                Download GST summary for selected month
                            </p>
                        </div>

                        <div className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-600">
                            {monthlyRemaining}/5 left
                        </div>

                    </div>


                    <div className="flex gap-3">

                        <select
                            value={selectedMonth}
                            onChange={(e) => {
                                setSelectedMonth(e.target.value);
                                checkMonthlyLimit(e.target.value);
                            }}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 outline-none"
                        >

                            <option value="">Select Month</option>

                            {monthOptions.map(m => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}

                        </select>

                        <button
                            onClick={downloadMonthly}
                            disabled={loadingMonthly || monthlyRemaining === 0}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition 
            ${loadingMonthly || monthlyRemaining === 0
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-green-600 hover:bg-green-700 text-white shadow-sm"
                                }`}
                        >
                            {loadingMonthly ? "Preparing..." : "Download"}
                        </button>

                    </div>

                    <p className="text-xs text-gray-400">
                        Remaining downloads this month: {monthlyRemaining} of 5
                    </p>

                </div>

            </div>


            <div className="text-xs text-gray-400 pt-4 border-t">
                Reports include taxable value, CGST, SGST, VAT and total invoice value for GST filing reference.
            </div>

        </div>

    );
};

export default GSTReports;